import type { ContentRepository } from "../../repositories/content/content-repository";
import type {
  Notice,
  Faq,
  Popup,
  Font,
  NoticeCreateRequest,
  NoticeUpdateRequest,
  FaqCreateRequest,
  FaqUpdateRequest,
  FaqOrderUpdateRequest,
  PopupReorderRequest,
  FontCreateRequest,
  FontUpdateRequest,
  Portfolio,
  PortfolioSection,
  PortfolioCreateRequest,
  PortfolioUpdateRequest,
  PortfolioReorderRequest,
  PortfolioButton,
  PortfolioLinkedOrder,
  PortfolioButtonsUpdateRequest,
  PortfolioOrderLinksUpdateRequest,
} from "../../types/content";
import { AppError } from "../../../shared/utils/error-handler";

export class ContentUseCase {
  constructor(private contentRepository: ContentRepository) {}

  // ========== 공지사항 ==========

  async getNotices(companyId: number): Promise<Notice[]> {
    try {
      return await this.contentRepository.getNotices(companyId);
    } catch (error) {
      throw new AppError(
        String(error) || "공지사항 목록을 불러오지 못했습니다.",
        "NOTICE_LIST_GET_FAILED",
        error
      );
    }
  }

  async getNotice(companyId: number, noticeId: number): Promise<Notice> {
    try {
      return await this.contentRepository.getNotice(companyId, noticeId);
    } catch (error) {
      throw new AppError(
        String(error) || "공지사항을 불러오지 못했습니다.",
        "NOTICE_GET_FAILED",
        error
      );
    }
  }

  async createNotice(
    companyId: number,
    request: NoticeCreateRequest
  ): Promise<Notice> {
    try {
      return await this.contentRepository.createNotice(companyId, request);
    } catch (error) {
      throw new AppError(
        String(error) || "공지사항 생성에 실패했습니다.",
        "NOTICE_CREATE_FAILED",
        error
      );
    }
  }

  async updateNotice(
    companyId: number,
    noticeId: number,
    request: NoticeUpdateRequest
  ): Promise<Notice> {
    try {
      return await this.contentRepository.updateNotice(
        companyId,
        noticeId,
        request
      );
    } catch (error) {
      throw new AppError(
        String(error) || "공지사항 수정에 실패했습니다.",
        "NOTICE_UPDATE_FAILED",
        error
      );
    }
  }

  async deleteNotice(companyId: number, noticeId: number): Promise<void> {
    try {
      await this.contentRepository.deleteNotice(companyId, noticeId);
    } catch (error) {
      throw new AppError(
        String(error) || "공지사항 삭제에 실패했습니다.",
        "NOTICE_DELETE_FAILED",
        error
      );
    }
  }

  // ========== FAQ ==========

  async getFaqs(companyId: number, category?: string): Promise<Faq[]> {
    try {
      return await this.contentRepository.getFaqs(companyId, category);
    } catch (error) {
      throw new AppError(
        String(error) || "FAQ 목록을 불러오지 못했습니다.",
        "FAQ_LIST_GET_FAILED",
        error
      );
    }
  }

  async getFaq(companyId: number, faqId: number): Promise<Faq> {
    try {
      return await this.contentRepository.getFaq(companyId, faqId);
    } catch (error) {
      throw new AppError(
        String(error) || "FAQ를 불러오지 못했습니다.",
        "FAQ_GET_FAILED",
        error
      );
    }
  }

  async createFaq(companyId: number, request: FaqCreateRequest): Promise<Faq> {
    try {
      return await this.contentRepository.createFaq(companyId, request);
    } catch (error) {
      throw new AppError(
        String(error) || "FAQ 생성에 실패했습니다.",
        "FAQ_CREATE_FAILED",
        error
      );
    }
  }

  async updateFaq(
    companyId: number,
    faqId: number,
    request: FaqUpdateRequest
  ): Promise<Faq> {
    try {
      return await this.contentRepository.updateFaq(companyId, faqId, request);
    } catch (error) {
      throw new AppError(
        String(error) || "FAQ 수정에 실패했습니다.",
        "FAQ_UPDATE_FAILED",
        error
      );
    }
  }

  async updateFaqOrder(
    companyId: number,
    faqId: number,
    request: FaqOrderUpdateRequest
  ): Promise<void> {
    try {
      await this.contentRepository.updateFaqOrder(companyId, faqId, request);
    } catch (error) {
      throw new AppError(
        String(error) || "FAQ 순서 변경에 실패했습니다.",
        "FAQ_ORDER_UPDATE_FAILED",
        error
      );
    }
  }

  async deleteFaq(companyId: number, faqId: number): Promise<void> {
    try {
      await this.contentRepository.deleteFaq(companyId, faqId);
    } catch (error) {
      throw new AppError(
        String(error) || "FAQ 삭제에 실패했습니다.",
        "FAQ_DELETE_FAILED",
        error
      );
    }
  }

  // ========== 팝업 ==========

  async getPopups(companyId: number): Promise<Popup[]> {
    try {
      return await this.contentRepository.getPopups(companyId);
    } catch (error) {
      throw new AppError(
        String(error) || "팝업 목록을 불러오지 못했습니다.",
        "POPUP_LIST_GET_FAILED",
        error
      );
    }
  }

  async createPopup(
    companyId: number,
    name: string,
    isEnabled: boolean,
    popupImage?: File
  ): Promise<Popup> {
    try {
      return await this.contentRepository.createPopup(
        companyId,
        name,
        isEnabled,
        popupImage
      );
    } catch (error) {
      throw new AppError(
        String(error) || "팝업 생성에 실패했습니다.",
        "POPUP_CREATE_FAILED",
        error
      );
    }
  }

  async updatePopup(
    companyId: number,
    popupId: number,
    name: string,
    isEnabled: boolean,
    popupImage?: File
  ): Promise<Popup> {
    try {
      return await this.contentRepository.updatePopup(
        companyId,
        popupId,
        name,
        isEnabled,
        popupImage
      );
    } catch (error) {
      throw new AppError(
        String(error) || "팝업 수정에 실패했습니다.",
        "POPUP_UPDATE_FAILED",
        error
      );
    }
  }

  async reorderPopups(
    companyId: number,
    request: PopupReorderRequest
  ): Promise<void> {
    try {
      await this.contentRepository.reorderPopups(companyId, request);
    } catch (error) {
      throw new AppError(
        String(error) || "팝업 순서 변경에 실패했습니다.",
        "POPUP_REORDER_FAILED",
        error
      );
    }
  }

  async deletePopup(companyId: number, popupId: number): Promise<void> {
    try {
      await this.contentRepository.deletePopup(companyId, popupId);
    } catch (error) {
      throw new AppError(
        String(error) || "팝업 삭제에 실패했습니다.",
        "POPUP_DELETE_FAILED",
        error
      );
    }
  }

  // ========== 폰트 ==========

  async getFonts(companyId: number): Promise<Font[]> {
    try {
      return await this.contentRepository.getFonts(companyId);
    } catch (error) {
      throw new AppError(
        String(error) || "폰트 목록을 불러오지 못했습니다.",
        "FONT_LIST_GET_FAILED",
        error
      );
    }
  }

  async createFont(companyId: number, request: FontCreateRequest): Promise<Font> {
    try {
      return await this.contentRepository.createFont(companyId, request);
    } catch (error) {
      throw new AppError(
        String(error) || "폰트 생성에 실패했습니다.",
        "FONT_CREATE_FAILED",
        error
      );
    }
  }

  async updateFont(
    companyId: number,
    fontId: number,
    request: FontUpdateRequest
  ): Promise<Font> {
    try {
      return await this.contentRepository.updateFont(companyId, fontId, request);
    } catch (error) {
      throw new AppError(
        String(error) || "폰트 수정에 실패했습니다.",
        "FONT_UPDATE_FAILED",
        error
      );
    }
  }

  async deleteFont(companyId: number, fontId: number): Promise<void> {
    try {
      await this.contentRepository.deleteFont(companyId, fontId);
    } catch (error) {
      throw new AppError(
        String(error) || "폰트 삭제에 실패했습니다.",
        "FONT_DELETE_FAILED",
        error
      );
    }
  }

  // ========== 포트폴리오 ==========

  async getPortfolios(companyId: number): Promise<Portfolio[]> {
    try {
      return await this.contentRepository.getPortfolios(companyId);
    } catch (error) {
      throw new AppError(
        String(error) || "포트폴리오 목록을 불러오지 못했습니다.",
        "PORTFOLIO_LIST_GET_FAILED",
        error
      );
    }
  }

  async getPortfolio(companyId: number, portfolioId: number): Promise<Portfolio> {
    try {
      return await this.contentRepository.getPortfolio(companyId, portfolioId);
    } catch (error) {
      throw new AppError(
        String(error) || "포트폴리오를 불러오지 못했습니다.",
        "PORTFOLIO_GET_FAILED",
        error
      );
    }
  }

  async createPortfolio(
    companyId: number,
    request: PortfolioCreateRequest,
    thumbnail?: File,
    images?: File[]
  ): Promise<Portfolio> {
    try {
      return await this.contentRepository.createPortfolio(
        companyId,
        request,
        thumbnail,
        images
      );
    } catch (error) {
      throw new AppError(
        String(error) || "포트폴리오 생성에 실패했습니다.",
        "PORTFOLIO_CREATE_FAILED",
        error
      );
    }
  }

  async updatePortfolio(
    companyId: number,
    portfolioId: number,
    request: PortfolioUpdateRequest,
    thumbnail?: File,
    images?: File[]
  ): Promise<Portfolio> {
    try {
      return await this.contentRepository.updatePortfolio(
        companyId,
        portfolioId,
        request,
        thumbnail,
        images
      );
    } catch (error) {
      throw new AppError(
        String(error) || "포트폴리오 수정에 실패했습니다.",
        "PORTFOLIO_UPDATE_FAILED",
        error
      );
    }
  }

  async reorderPortfolios(
    companyId: number,
    request: PortfolioReorderRequest
  ): Promise<void> {
    try {
      await this.contentRepository.reorderPortfolios(companyId, request);
    } catch (error) {
      throw new AppError(
        String(error) || "포트폴리오 순서 변경에 실패했습니다.",
        "PORTFOLIO_REORDER_FAILED",
        error
      );
    }
  }

  async deletePortfolio(companyId: number, portfolioId: number): Promise<void> {
    try {
      await this.contentRepository.deletePortfolio(companyId, portfolioId);
    } catch (error) {
      throw new AppError(
        String(error) || "포트폴리오 삭제에 실패했습니다.",
        "PORTFOLIO_DELETE_FAILED",
        error
      );
    }
  }

  async getPortfolioSection(companyId: number): Promise<PortfolioSection> {
    try {
      return await this.contentRepository.getPortfolioSection(companyId);
    } catch (error) {
      throw new AppError(
        String(error) || "포트폴리오 섹션 설정을 불러오지 못했습니다.",
        "PORTFOLIO_SECTION_GET_FAILED",
        error
      );
    }
  }

  async updatePortfolioSection(
    companyId: number,
    isEnabled: boolean
  ): Promise<PortfolioSection> {
    try {
      return await this.contentRepository.updatePortfolioSection(companyId, isEnabled);
    } catch (error) {
      throw new AppError(
        String(error) || "포트폴리오 섹션 설정 변경에 실패했습니다.",
        "PORTFOLIO_SECTION_UPDATE_FAILED",
        error
      );
    }
  }

  async updatePortfolioButtons(
    companyId: number,
    portfolioId: number,
    request: PortfolioButtonsUpdateRequest
  ): Promise<PortfolioButton[]> {
    try {
      return await this.contentRepository.updatePortfolioButtons(
        companyId,
        portfolioId,
        request
      );
    } catch (error) {
      throw new AppError(
        String(error) || "포트폴리오 버튼 저장에 실패했습니다.",
        "PORTFOLIO_BUTTONS_UPDATE_FAILED",
        error
      );
    }
  }

  async updatePortfolioOrderLinks(
    companyId: number,
    portfolioId: number,
    request: PortfolioOrderLinksUpdateRequest
  ): Promise<PortfolioLinkedOrder[]> {
    try {
      return await this.contentRepository.updatePortfolioOrderLinks(
        companyId,
        portfolioId,
        request
      );
    } catch (error) {
      throw new AppError(
        String(error) || "포트폴리오 주문 연결 저장에 실패했습니다.",
        "PORTFOLIO_ORDER_LINKS_UPDATE_FAILED",
        error
      );
    }
  }
}
